import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Trash2, DollarSign, ChevronDown, ChevronUp, Briefcase, TrendingDown, Pencil } from 'lucide-react';
import { useSortableFilterable } from '@/hooks/useSortableFilterable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PurchaseOrders() {
  const { token, user } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingPO, setDeletingPO] = useState(null);
  const [editingPO, setEditingPO] = useState(null);
  const [expandedPO, setExpandedPO] = useState(null);

  const [formData, setFormData] = useState({
    customerId: '',
    poNumber: '',
    originalAmount: '',
    linkedJobIds: [],
    notes: '',
  });

  const isOfficeOrOwner = user?.role === 'OFFICE' || user?.role === 'OWNER';

  const { sortColumn, sortDir, handleSort, searchQuery, setSearchQuery, sortedData } = useSortableFilterable(purchaseOrders, 'poNumber', 'asc');

  const filteredData = sortedData.filter(po => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      po.poNumber?.toLowerCase().includes(q) ||
      po.customerName?.toLowerCase().includes(q) ||
      po.notes?.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [poRes, custRes, jobsRes] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setPurchaseOrders(poRes.data);
      setCustomers(custRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerJobs = (customerId) => {
    return jobs.filter(j => j.customerId === customerId);
  };

  const handleOpenDialog = (po = null) => {
    if (po) {
      setEditingPO(po);
      setFormData({
        customerId: po.customerId,
        poNumber: po.poNumber,
        originalAmount: po.originalAmount.toString(),
        linkedJobIds: po.linkedJobIds || [],
        notes: po.notes || '',
      });
    } else {
      setEditingPO(null);
      setFormData({
        customerId: '',
        poNumber: '',
        originalAmount: '',
        linkedJobIds: [],
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId || !formData.poNumber || !formData.originalAmount) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const payload = {
        ...formData,
        originalAmount: parseFloat(formData.originalAmount),
      };

      if (editingPO) {
        await axios.patch(`${API}/purchase-orders/${editingPO.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Purchase order updated');
      } else {
        await axios.post(`${API}/purchase-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Purchase order created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchase order');
    }
  };

  const handleDelete = async () => {
    if (!deletingPO) return;
    try {
      await axios.delete(`${API}/purchase-orders/${deletingPO.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Purchase order deleted');
      setDeleteOpen(false);
      setDeletingPO(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete purchase order');
    }
  };

  const toggleJobLink = (jobId) => {
    setFormData(prev => ({
      ...prev,
      linkedJobIds: prev.linkedJobIds.includes(jobId)
        ? prev.linkedJobIds.filter(id => id !== jobId)
        : [...prev.linkedJobIds, jobId]
    }));
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="text-center text-slate-600">Loading purchase orders...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8" data-testid="purchase-orders-page">
      <div className="flex flex-col gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">Purchase Orders</h1>
          <p className="mt-1 md:mt-2 text-base md:text-lg text-slate-700">
            Track client PO balances across multiple jobs
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            data-testid="po-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search PO number, customer..."
            className="w-full sm:w-[300px] rounded-sm border-2"
          />
          {isOfficeOrOwner && (
            <Button
              onClick={() => handleOpenDialog()}
              data-testid="create-po-button"
              className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm shadow-sm transition-all active:scale-95 py-4 md:py-2 text-base w-full sm:w-auto"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Purchase Order
            </Button>
          )}
        </div>
      </div>

      {filteredData.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <DollarSign className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-4">No purchase orders yet</p>
            {isOfficeOrOwner && (
              <Button onClick={() => handleOpenDialog()} variant="outline" className="border-2 border-[#1A3668] text-[#1A3668] hover:bg-slate-50 rounded-sm">
                Create your first PO
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredData.map((po) => {
            const usagePct = po.originalAmount > 0 ? (po.amountUsed / po.originalAmount) * 100 : 0;
            const isOverBudget = po.remainingBalance < 0;
            return (
              <Card
                key={po.id}
                data-testid={`po-card-${po.id}`}
                className={`border-slate-200 shadow-sm rounded-sm ${isOverBudget ? 'border-red-300' : ''}`}
              >
                <CardContent className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-xl font-extrabold text-slate-900">{po.poNumber}</h3>
                        <Badge variant="outline" className="rounded-sm text-xs bg-[#1A3668]/5 text-[#1A3668] border-[#1A3668]/20">
                          {po.customerName}
                        </Badge>
                        {isOverBudget && (
                          <Badge className="bg-red-100 text-red-800 border-red-300 rounded-sm text-xs">
                            <TrendingDown className="w-3 h-3 mr-1" /> Over Budget
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-600 flex-wrap">
                        <Briefcase className="w-3 h-3" />
                        {po.linkedJobNumbers?.length > 0
                          ? po.linkedJobNumbers.join(', ')
                          : 'No jobs linked'}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 min-w-[360px]">
                      <div className="text-center p-3 bg-slate-50 rounded-sm border border-slate-200">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Original</p>
                        <p className="text-lg font-extrabold text-slate-900">${po.originalAmount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className="text-center p-3 bg-slate-50 rounded-sm border border-slate-200">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Used</p>
                        <p className="text-lg font-extrabold text-amber-700">${po.amountUsed?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                      <div className={`text-center p-3 rounded-sm border ${isOverBudget ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Remaining</p>
                        <p className={`text-lg font-extrabold ${isOverBudget ? 'text-red-700' : 'text-green-700'}`}>
                          ${po.remainingBalance?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Usage bar */}
                  <div className="mt-4">
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all rounded-full ${usagePct > 100 ? 'bg-red-500' : usagePct > 80 ? 'bg-amber-500' : 'bg-green-500'}`}
                        style={{ width: `${Math.min(usagePct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1 text-right">{usagePct.toFixed(1)}% used</p>
                  </div>

                  <div className="flex gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
                      data-testid={`expand-po-${po.id}`}
                      className="border-2 border-slate-200 hover:border-[#1A3668] rounded-sm"
                    >
                      {expandedPO === po.id ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
                      {expandedPO === po.id ? 'Collapse' : 'Job Breakdown'}
                    </Button>
                    {isOfficeOrOwner && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenDialog(po)}
                          data-testid={`edit-po-${po.id}`}
                          className="border-2 border-slate-200 rounded-sm"
                        >
                          <Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setDeletingPO(po); setDeleteOpen(true); }}
                          data-testid={`delete-po-${po.id}`}
                          className="border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>

                  {expandedPO === po.id && (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-3">Cost Breakdown by Job</p>
                      {po.jobBreakdown?.length > 0 ? (
                        <table className="w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-slate-100 border-b-2 border-slate-200">
                              <th className="p-3 text-left font-bold uppercase tracking-wider text-slate-700 text-xs">Job</th>
                              <th className="p-3 text-left font-bold uppercase tracking-wider text-slate-700 text-xs">Description</th>
                              <th className="p-3 text-right font-bold uppercase tracking-wider text-slate-700 text-xs">Amount Used</th>
                            </tr>
                          </thead>
                          <tbody>
                            {po.jobBreakdown.map((jb, idx) => (
                              <tr key={jb.jobNumber || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="p-3 font-bold text-[#1A3668]">{jb.jobNumber}</td>
                                <td className="p-3 text-slate-600">{jb.description}</td>
                                <td className="p-3 text-right font-mono font-bold text-slate-900">${jb.amountUsed?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-sm text-slate-500 italic">No labor entries recorded yet for linked jobs</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[550px] rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-[#1A3668]" />
              {editingPO ? 'Edit Purchase Order' : 'New Purchase Order'}
            </DialogTitle>
            <DialogDescription>
              Create a PO and link it to one or more jobs. Balance tracks across all linked jobs.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Customer *</Label>
                <Select
                  value={formData.customerId}
                  onValueChange={(v) => setFormData({ ...formData, customerId: v, linkedJobIds: [] })}
                >
                  <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="po-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">PO Number *</Label>
                  <Input
                    data-testid="po-number-input"
                    value={formData.poNumber}
                    onChange={(e) => setFormData({ ...formData, poNumber: e.target.value })}
                    placeholder="e.g., PO-2026-001"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Original Amount *</Label>
                  <Input
                    data-testid="po-amount-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.originalAmount}
                    onChange={(e) => setFormData({ ...formData, originalAmount: e.target.value })}
                    placeholder="150000.00"
                    required
                    className="mt-2 rounded-sm border-2"
                  />
                </div>
              </div>

              {formData.customerId && (
                <div>
                  <Label className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                    Link Jobs ({formData.linkedJobIds.length} selected)
                  </Label>
                  <div className="border-2 border-slate-200 rounded-sm max-h-[200px] overflow-y-auto">
                    {getCustomerJobs(formData.customerId).length > 0 ? (
                      getCustomerJobs(formData.customerId).map(job => (
                        <label
                          key={job.id}
                          className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                        >
                          <Checkbox
                            data-testid={`link-job-${job.id}`}
                            checked={formData.linkedJobIds.includes(job.id)}
                            onCheckedChange={() => toggleJobLink(job.id)}
                          />
                          <div className="flex-1">
                            <span className="font-bold text-[#1A3668] text-sm">{job.jobNumber}</span>
                            <span className="text-slate-500 text-xs ml-2">{job.description}</span>
                          </div>
                        </label>
                      ))
                    ) : (
                      <p className="p-3 text-sm text-slate-500 italic">No jobs found for this customer</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-bold uppercase tracking-wider text-slate-500">Notes</Label>
                <Input
                  data-testid="po-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional notes"
                  className="mt-2 rounded-sm border-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-2 border-slate-200 rounded-sm">
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-po-button"
                className="bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm"
              >
                {editingPO ? 'Update PO' : 'Create PO'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingPO?.poNumber}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-po"
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
